import { GroupedMap, Log, Progress } from "../utils";
import { Lock } from "../utils/async";
import { ERR_INVALID_ARGUMENT } from "../utils/error";
import { stringifyToJson } from "./io/Serialization";
import { DOCUMENT_MAJOR_VERSION, DOCUMENT_MINOR_VERSION } from "./io/Types";
import { ZipDocumentContainer } from "./io/ZipDocumentContainer";
import { addGeometriesToContainer, readGeometriesFromContainer } from "./mdl/internal/GeometrySerialization";
import { addTexturesToContainer, readTexturesFromContainer } from "./mdl/internal/MaterialSerialization";
import { deserializeModel, serializeModel } from "./mdl/internal/ModelSerialization";
export function loadDocumentFromZip(pDocument, pZipData, pOptions) {
    const tProgress = new Progress(pOptions != null ? pOptions.onProgress : null, true);
    tProgress.startSection(5); //for loadContainer
    //copy options to set new progress callback
    const tCopiedOptions = {};
    Object.assign(tCopiedOptions, pOptions);
    tCopiedOptions.onProgress = (pProgress) => {
        tProgress.setProgress(pProgress);
    };
    const tPromise = ZipDocumentContainer.LoadContainer(pZipData).then((pContainer) => {
        tProgress.startSection(95);
        return (loadDocumentFromContainer(pDocument, pContainer, tCopiedOptions));
    });
    return (tPromise);
}
export async function loadDocumentFromContainer(pDocument, pContainer, pOptions) {
    if (pContainer == null)
        throw new Error(ERR_INVALID_ARGUMENT);
    const tApp = pDocument.app;
    let tLoadFailed = true;
    const tLockContext = await gLoadSaveLock.acquireLock();
    try {
        pDocument.eventBeforeLoad.trigger();
        tApp.model.beginModification();
        tApp.viewer.beginDisableRendering();
        //setup progress
        const tProgress = new Progress(pOptions != null ? pOptions.onProgress : null, true);
        const tProgressCallback = (pProgress) => {
            tProgress.setProgress(pProgress);
        };
        tProgress.startSection(5); //5% for header and app data
        //read document and validate
        const tDoc = await readDocumentFromContainer(pContainer);
        validateDocument(tDoc);
        //load app data
        const tAppData = new GroupedMap();
        tAppData.deserialize(tDoc.appData);
        //custom validation
        if (pOptions != null && pOptions.onValidateDocument != null)
            await pOptions.onValidateDocument(tDoc.header, tAppData);
        //document is valid, clear the current document
        pDocument.clear();
        const tContext = new InputContext(pContainer, tDoc.model, tDoc.header);
        //assign app data
        pDocument._setAppData(tAppData);
        //module data
        tProgress.startSection(20); //20% module data
        for (const tModule of tApp.modules) {
            let tData = null;
            if (tDoc.moduleData != null)
                tData = tDoc.moduleData[tModule.moduleId];
            await tModule.loadDocumentData(tContext, tData);
        }
        //viewer
        tApp.viewer.deserialize(tDoc.viewer);
        //pre-load geometries
        tProgress.startSection(30); //30% for geometries
        await readGeometriesFromContainer(tApp.model.geometries, tDoc.model.geometries, pContainer, tProgressCallback);
        //pre-load textures
        tProgress.startSection(30); //30% for textures
        await readTexturesFromContainer(tApp.model.materials, tDoc.model.textures, pContainer, tProgressCallback);
        tProgress.startSection(15); //15% model + pending tasks
        //model
        deserializeModel(tApp.model, tDoc.model, tContext);
        //process pending tasks
        await tContext.waitForPendingTasks();
        //log missing geometries and textures
        tApp.model.geometries.missingGeometries.forEach((pId) => {
            Log.warn("Missing geometry: " + pId);
        });
        tApp.model.materials.missingResourceTextures.forEach((pId) => {
            Log.warn("Missing texture: " + pId);
        });
        tLoadFailed = false;
    }
    finally {
        if (tLoadFailed) {
            pDocument.clear();
        }
        tApp.viewer.endDisableRendering();
        tApp.model.endModification();
        pDocument.eventAfterLoad.trigger(tLoadFailed);
        tApp.viewer.requestRenderFrame();
        tLockContext.unlock();
    }
}
export function saveDocumentToZip(pDocument, pOptions) {
    const tProgress = new Progress(pOptions != null ? pOptions.onProgress : null, true);
    tProgress.startSection(30); //for saveToContainer
    //copy options to set new progress callback
    const tCopiedOptions = {};
    Object.assign(tCopiedOptions, pOptions);
    tCopiedOptions.onProgress = (pProgress) => {
        tProgress.setProgress(pProgress);
    };
    const tContainer = ZipDocumentContainer.NewContainer();
    if (pOptions != null && pOptions.compressionLevel != null)
        tContainer.compressionLevel = pOptions.compressionLevel;
    const tPromise = saveDocumentToContainer(pDocument, tContainer, tCopiedOptions).then(() => {
        tProgress.startSection(70);
        return (tContainer.generateZip(tCopiedOptions.onProgress));
    });
    return (tPromise);
}
export async function saveDocumentToContainer(pDocument, pContainer, pOptions) {
    if (pContainer == null)
        throw new Error(ERR_INVALID_ARGUMENT);
    const tApp = pDocument.app;
    let tSaveFailed = true;
    const tLockContext = await gLoadSaveLock.acquireLock();
    try {
        pDocument.eventBeforeSave.trigger();
        //get options
        const tOptions = {};
        tOptions.embedGeometries = false;
        tOptions.embedTextures = false;
        tOptions.embedAppModuleResources = true;
        if (pOptions != null) {
            Object.assign(tOptions, pOptions);
        }
        //setup progress
        let tEmbedStepsCount = 0;
        if (tOptions.embedGeometries)
            tEmbedStepsCount++;
        if (tOptions.embedTextures)
            tEmbedStepsCount++;
        const tEmbedProgressSize = 30 * tEmbedStepsCount; //30% per step
        const tProgressSize = 100 - tEmbedProgressSize;
        const tProgressCallback = (pProgress) => {
            tProgress.setProgress(pProgress);
        };
        const tProgress = new Progress(tOptions.onProgress, true);
        tProgress.startSection(tProgressSize * 0.1); //for header and app data
        const tContext = new OutputContext(pContainer);
        const tDoc = {};
        //header
        tDoc.header = saveHeader(tApp);
        //app data
        const tAppData = pDocument.appData.serialize();
        if (tAppData != null)
            tDoc.appData = tAppData;
        //module data
        tProgress.startSection(tProgressSize * 0.4); //module data
        if (tApp.modules.length > 0) {
            tDoc.moduleData = {};
            const tModuleCount = tApp.modules.length;
            for (let i = 0; i < tModuleCount; ++i) {
                const tModule = tApp.modules[i];
                const tData = await tModule.saveDocumentData(tContext, tOptions);
                if (tData != null) {
                    tDoc.moduleData[tModule.moduleId] = tData;
                }
                tProgress.setProgress(((i + 1) / tModuleCount) * 100.0);
            }
        }
        tProgress.startSection(tProgressSize * 0.4); //for viewer + model
        //viewer
        tDoc.viewer = tApp.viewer.serialize();
        //model
        tDoc.model = serializeModel(tApp.model, tContext);
        tProgress.startSection(tProgressSize * 0.1); //for pending tasks
        //process pending tasks
        await tContext.waitForPendingTasks();
        //embed document.json
        const tDocJson = stringifyToJson(tDoc);
        await pContainer.addFile(tDocJson, DOCUMENT_FILE);
        //embed geometries
        if (tOptions.embedGeometries) {
            tProgress.startSection(tEmbedProgressSize / tEmbedStepsCount);
            await addGeometriesToContainer(tApp.model.geometries, tContext.geometryIds, pContainer, tProgressCallback);
        }
        //embed textures
        if (tOptions.embedTextures) {
            tProgress.startSection(tEmbedProgressSize / tEmbedStepsCount);
            await addTexturesToContainer(tApp.model.materials, tContext.textureIds, pContainer, tProgressCallback);
        }
        tSaveFailed = false;
    }
    finally {
        pDocument.eventAfterSave.trigger(tSaveFailed);
        tLockContext.unlock();
    }
}
//// internals /////////////////////////////////////////////////////////////////////////////////////
const DOCUMENT_FILE = "document.json";
const gLoadSaveLock = new Lock();
function saveHeader(pApp) {
    const tHeader = {};
    tHeader.majorVer = DOCUMENT_MAJOR_VERSION;
    tHeader.minorVer = DOCUMENT_MINOR_VERSION;
    if (pApp.applicationName !== "")
        tHeader.appName = pApp.applicationName;
    if (pApp.applicationVersion !== "")
        tHeader.appVer = pApp.applicationVersion;
    return (tHeader);
}
function readDocumentFromContainer(pContainer) {
    if (pContainer == null)
        return (Promise.reject(new Error(ERR_INVALID_ARGUMENT)));
    const tPromise = pContainer.getFile(DOCUMENT_FILE, false).then((pJson) => {
        return (JSON.parse(pJson));
    }).catch((pError) => {
        return (Promise.reject(new Error("Failed to read " + DOCUMENT_FILE
            + ": " + pError)));
    });
    return (tPromise);
}
function validateDocument(pDoc) {
    if (pDoc == null || pDoc.header == null || pDoc.model == null || pDoc.model.elements == null)
        throw new Error("invalid file");
    const tMajorVer = pDoc.header.majorVer;
    if (tMajorVer == null)
        throw new Error("unknown file version");
    if (tMajorVer > DOCUMENT_MAJOR_VERSION)
        throw new Error("unsupported file version: " + tMajorVer);
}
/**
 * IOContext
 */
class BaseIOContext {
    get customState() {
        return (this.mCustomState);
    }
    constructor(pContainer) {
        this.mContainer = pContainer;
        this.mPendingTasks = new Array();
        this.mCustomState = new GroupedMap();
    }
    registerPendingTask(pTask) {
        if (pTask != null)
            this.mPendingTasks.push(pTask);
    }
    async waitForPendingTasks() {
        for (const tTask of this.mPendingTasks) {
            try {
                await tTask();
            }
            catch (pError) {
                console.log("Pending task failed.", pError);
            }
        }
    }
}
/**
 * LoadContext
 */
class InputContext extends BaseIOContext {
    constructor(pContainer, pIOModel, pDocHeader) {
        super(pContainer);
        this.mIOModel = pIOModel;
        this.mDocHeader = pDocHeader;
    }
    get documentHeader() {
        return (this.mDocHeader);
    }
    getGeometryIdByIndex(pIndex) {
        return (this.mIOModel != null ? this.mIOModel.geometries[pIndex].id : null);
    }
    getMaterialIdByIndex(pIndex) {
        return (this.mIOModel != null ? this.mIOModel.materials[pIndex].id : null);
    }
    getTextureIdByIndex(pIndex) {
        return (this.mIOModel != null ? this.mIOModel.textures[pIndex].id : null);
    }
    getLayerNameByIndex(pIndex) {
        return (this.mIOModel != null ? this.mIOModel.layers[pIndex].name : null);
    }
    getResource(pFilename, pBinary) {
        return (this.mContainer.getFile("resources/" + pFilename, pBinary));
    }
    getFile(pFilename, pBinary) {
        return (this.mContainer.getFile(pFilename, pBinary));
    }
    listFiles(pFolderPath) {
        return (this.mContainer.listFiles(pFolderPath));
    }
}
/**
 * OutputContext
 */
class OutputContext extends BaseIOContext {
    get geometryIds() {
        return (this.mGeometryIds);
    }
    get materialIds() {
        return (this.mMaterialIds);
    }
    get textureIds() {
        return (this.mTextureIds);
    }
    get layerNames() {
        return (this.mLayerNames);
    }
    constructor(pContainer) {
        super(pContainer);
        this.mGeometryIds = new Array();
        this.mGeometryIdsMap = new Map();
        this.mMaterialIds = new Array();
        this.mMaterialIdsMap = new Map();
        this.mTextureIds = new Array();
        this.mTextureIdsMap = new Map();
        this.mLayerNames = new Array();
        this.mLayerNamesMap = new Map();
    }
    addGeometry(pGeomId) {
        return (this.addGraphic(pGeomId, this.mGeometryIds, this.mGeometryIdsMap));
    }
    addMaterial(pMatId) {
        return (this.addGraphic(pMatId, this.mMaterialIds, this.mMaterialIdsMap));
    }
    addTexture(pTexId) {
        return (this.addGraphic(pTexId, this.mTextureIds, this.mTextureIdsMap));
    }
    addLayer(pName) {
        return (this.addGraphic(pName, this.mLayerNames, this.mLayerNamesMap));
    }
    addResource(pData, pFilename, pCompress) {
        return (this.mContainer.addFile(pData, "resources/" + pFilename, pCompress));
    }
    addResourceFromUrl(pUrl, pFilename, pBinary, pCompress) {
        return (this.mContainer.addFileFromUrl(pUrl, "resources/" + pFilename, pBinary, pCompress));
    }
    addResourceFromCache(pFilename, pCompress) {
        return (this.mContainer.addFileFromCache("resources/" + pFilename, pCompress));
    }
    addGraphic(pId, pArray, pMap) {
        let tIndex = pMap.get(pId);
        if (tIndex != null) {
            return (tIndex);
        }
        else {
            tIndex = pArray.push(pId) - 1;
            pMap.set(pId, tIndex);
        }
        return (tIndex);
    }
}
